import { IGetVenuesResponseData } from 'src/api/getVenues/types';

export interface ILiveMenu {
  menuId: string;
  name: string;
  venueNames: string[];
  channelNames: string[];
  lastPublishedDate: string;
}

/**
 * Parses the API's timestamp format ('2022-03-30 12:35:06.690682' — space
 * separator with microsecond precision) into epoch milliseconds. Normalising to
 * a strict ISO 8601 string keeps parsing consistent across browsers; unparseable
 * values fall back to 0 so they sort oldest rather than producing NaN.
 */
const toEpoch = (value: string): number => {
  const isoLike = value.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  const ms = Date.parse(isoLike);
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Aggregates the menus that are currently published, across all venues and
 * channels, from the published_channels already returned by GET /venues.
 */
export const getLiveMenus = (venues: IGetVenuesResponseData[]): ILiveMenu[] => {
  const liveMenus = new Map<string, ILiveMenu>();

  venues.forEach((venue) => {
    (venue.published_channels ?? []).forEach((channel) => {
      channel.menus.forEach((menu) => {
        const existing = liveMenus.get(menu.menu_id) ?? {
          menuId: menu.menu_id,
          name: menu.name,
          venueNames: [],
          channelNames: [],
          lastPublishedDate: menu.last_published_date,
        };

        if (!existing.venueNames.includes(venue.name)) existing.venueNames.push(venue.name);
        if (!existing.channelNames.includes(channel.name)) existing.channelNames.push(channel.name);
        if (toEpoch(menu.last_published_date) > toEpoch(existing.lastPublishedDate)) {
          existing.lastPublishedDate = menu.last_published_date;
        }

        liveMenus.set(menu.menu_id, existing);
      });
    });
  });

  return [...liveMenus.values()].sort((a, b) => toEpoch(b.lastPublishedDate) - toEpoch(a.lastPublishedDate));
};
